import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { requestReassignUserItems } from "../../../actions/userActions";
import { hideModal } from "../../../actions/actionCreators";
import type { User } from "../../../types/types";
import { getArrayOfAllUsers } from "../../../reducers/users";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import MaterialModal from "../MaterialModal";
import FormError from "../../misc/formErrors";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import withModal from "../withModal";

const UserReassignItemsModal = ({ modalProps, isOpen }) => {
    const dispatch = useDispatch();

    const [selectedUser, setSelectedUser] = useState(0);
    const [iAgreeValue, setIAgreeValue] = useState("");
    const [errors, setErrors] = useState({});

    const user = useSelector(state => state.users.byId[modalProps.userId || 0]);
    const users = useSelector(state => getArrayOfAllUsers(state));

    const fromName = user
        ? (user.FirstName || "") + " " + (user.LastName || "") + " (" + (user.Email || "") + ") (" + user.Id + ")"
        : "";

    const renderUsersSelect = () => {
        const filteredUsers = users.filter(x => x.Id != modalProps.userId);

        return (
            <FormControl>
                <InputLabel id="select-label">Reassign To</InputLabel>
                <Select
                    labelid="select-label"
                    onChange={e => setSelectedUser(e.target.value)}
                    value={selectedUser || ""}
                >
                    <MenuItem disabled value="">
                        <em>Select...</em>
                    </MenuItem>
                    {filteredUsers.map(u => renderUsersSelectOption(u))}
                </Select>
            </FormControl>
        );
    };

    const renderUsersSelectOption = (user: User) => {
        const optionText =
            (user.FirstName || "") + " " + (user.LastName || "") + " (" + (user.Email || "") + ") (" + user.Id + ")";
        return (
            <MenuItem key={user.Id} value={user.Id}>
                {optionText}
            </MenuItem>
        );
    };

    const hide = () => {
        setSelectedUser(0);
        setIAgreeValue("");
        setErrors({});
        dispatch(hideModal("USER_REASSIGN_ITEMS"));
    };

    const validate = () => {
        let errors = {};

        if (!selectedUser) {
            errors.selectedUser = "User selection is required.";
        }
        if (iAgreeValue != "I agree") {
            errors.iAgreeValue = "'I agree' confirmation is required.";
        }

        setErrors(errors);
        return Object.keys(errors).length == 0;
    };

    const handleReassignItemsClick = () => {
        if (validate()) {
            dispatch(requestReassignUserItems(modalProps.userId, selectedUser));
            hide();
        }
    };

    return user ? (
        <MaterialModal
            isOpen={isOpen}
            title="Reassign Items to Another User"
            primaryButtonText="Reassign All Data"
            innerDescription="The system will move all items from this user to another user. This action is IRREVERSIBLE. You must be 100% sure you wish to proceed."
            onClose={hide}
            onPrimaryButtonRequest={handleReassignItemsClick}
            id="user-reassign-modal"
        >
            <div style={{ display: "flex", flexDirection: "column" }}>
                {(errors.selectedUser || errors.iAgreeValue) && (
                    <FormError messages={[errors.selectedUser].concat([errors.iAgreeValue])} type="error" />
                )}
                <TextField label="From" type="text" margin="normal" defaultValue={fromName} disabled />
                {renderUsersSelect()}
                <TextField
                    label="You must type 'I agree' to proceed"
                    type="text"
                    margin="normal"
                    value={iAgreeValue}
                    onChange={e => setIAgreeValue(e.target.value)}
                />
            </div>
        </MaterialModal>
    ) : null;
};

UserReassignItemsModal.propTypes = {
    modalProps: PropTypes.object,
    isOpen: PropTypes.bool,
    requestReassignUserItems: PropTypes.func,
    hideModal: PropTypes.func,
};

export default withModal(UserReassignItemsModal, "USER_REASSIGN_ITEMS");
